"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useShift = exports.ShiftProvider = exports.SAVING_SHIFT_ACTION = exports.LOADING_SHIFT_ACTION = exports.LOADING_DUMMY_SHIFT_ACTION = exports.ShiftContext = exports.DISABLED_EDITATION = exports.formHasPosition = void 0;
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const filtered_content_for_organization_1 = require("@app/util/filtered-content-for-organization");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const get_shift_default_details_1 = require("./get-shift-default-details");
const use_copies_middleware_1 = require("./util/middleware/use-copies-middleware");
const use_dummy_shift_middleware_1 = require("./util/middleware/use-dummy-shift-middleware");
const use_initial_load_middleware_1 = require("./util/middleware/use-initial-load-middleware");
const use_save_shift_middleware_1 = require("./util/middleware/use-save-shift-middleware");
const use_section_toggled_middleware_1 = require("./util/middleware/use-section-toggled-middleware");
const use_shift_template_middleware_1 = require("./util/middleware/use-shift-template-middleware");
const use_sync_shift_middleware_1 = require("./util/middleware/use-sync-shift-middleware");
const use_transfer_middleware_1 = require("./util/middleware/use-transfer-middleware");
const formHasPosition = (t) => (0, util_1.isValue)(t.position);
exports.formHasPosition = formHasPosition;
exports.DISABLED_EDITATION = Object.freeze({
    DUE_TO_LOCKED_PERIOD: 'disabled-due-to-locked-period',
    DUE_TO_MISSING_TEMPLATE_WRITE_PERMISSION: 'disabled-due-to-missing-template-write-permission',
    DUE_TO_TRANSFER: 'disabled-due-to-transfer',
});
exports.ShiftContext = react_1.default.createContext(undefined);
exports.LOADING_DUMMY_SHIFT_ACTION = 'loading-dummy-shift';
exports.LOADING_SHIFT_ACTION = 'loading-shift';
exports.SAVING_SHIFT_ACTION = 'saving-shift';
const ShiftProvider = ({ children, initialData }) => {
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const loading = (0, store_1.useRootSelector)((state) => state.loading.includes(exports.LOADING_DUMMY_SHIFT_ACTION));
    const [initialLoad, setInitialLoad] = react_1.default.useState(true);
    const saving = (0, store_1.useRootSelector)((state) => state.loading.includes(exports.SAVING_SHIFT_ACTION));
    const getShiftDefaultDetails = (0, get_shift_default_details_1.useGetShiftDefaultDetails)();
    const { control, setValue, reset, getValues, handleSubmit, trigger } = (0, react_hook_form_1.useForm)({
        defaultValues: getShiftDefaultDetails(initialData?.data),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });
    const [selectedTab, setSelectedTab] = react_1.default.useState('overview');
    const [toggledSections, setToggledSections] = react_1.default.useState(['automatic-pause']);
    const [transferGroups] = react_1.default.useState(() => {
        const result = [];
        workspace?.transferGroups?.forEach((group) => {
            group?.workspaces?.forEach((workspace) => {
                if (workspace?.name) {
                    result.push({
                        value: workspace.id,
                        label: workspace.name,
                        groupTitle: group.name || undefined,
                    });
                }
            });
        });
        return result;
    });
    const [existingShiftData, setExistingShiftData] = react_1.default.useState();
    const { targetWsId, setTargetWsId, transferRole, transferStatus, transferType, transferMaster, transferSlave, } = (0, use_transfer_middleware_1.useTransferMiddleware)({
        initialData,
        existingShiftData,
        setToggledSections,
        toggledSections,
    });
    const [disabledEditation, setDisabledEditation] = react_1.default.useState();
    react_1.default.useEffect(() => {
        if (disabledEditation)
            return;
        if (transferStatus === 'pending' || transferRole === 'slave') {
            setDisabledEditation('disabled-due-to-transfer');
        }
    }, [disabledEditation, transferStatus, transferRole]);
    const syncShiftSections = (0, use_sync_shift_middleware_1.useSyncShiftMiddleware)({
        setToggledSections,
    });
    const [errors, setErrors] = react_1.default.useState();
    const { loadDummyShift, dummyShift } = (0, use_dummy_shift_middleware_1.useDummyShiftMiddlewareProps)({
        trigger,
        getValues,
        control,
        initialData,
        initialLoad,
        setErrors,
        variant: transferRole === 'slave' ? 'transferSlave' : 'default',
    });
    (0, use_initial_load_middleware_1.useInitialLoadMiddleware)({
        loadDummyShift,
        syncShiftSections,
        initialData,
        reset,
        setInitialLoad,
        setDisabledEditation,
        setExistingShiftData: (shiftData) => {
            setExistingShiftData(shiftData);
        },
    });
    const { add, remove, triggerSubscriptions } = (0, use_section_toggled_middleware_1.useSectionToggledMiddleware)();
    const onSubmit = (0, use_save_shift_middleware_1.useSaveShiftMiddleware)({ initialData });
    const { currentTemplate, templateName, setTemplateName, applyShiftTemplate } = (0, use_shift_template_middleware_1.useShiftTemplateMiddleware)({
        initialData,
        loadDummyShift,
        reset,
        getValues,
        getShiftDefaultDetails,
        syncShiftSections,
    });
    const { copiesCount, setCopiesCount } = (0, use_copies_middleware_1.useCopiesMiddleware)();
    const toggleSection = react_1.default.useCallback((section, visible) => {
        setToggledSections((prev) => {
            if (!visible) {
                triggerSubscriptions(section);
                const _index = prev.findIndex((sec) => sec === section);
                // if we are turning off automatic pause, we have to add manual pause instead
                if (section === 'automatic-pause') {
                    // set pauses to fixed and add default shift breaks
                    setValue('pausesFixed', true);
                    const defaultPauses = dummyShift?.pauses ||
                        util_1.timeUtil.getDefaultShiftBreaks({
                            details: getValues,
                            workspace,
                        });
                    setValue('pauses', defaultPauses);
                    return ['manual-pause'].concat(prev.slice(0, _index).concat(prev.slice(_index + 1, undefined)));
                }
                // if we are turning off emergency we have to turn off standby flag on shift
                if (section === 'emergency') {
                    setValue('standBy', false);
                }
                if (section === 'overtime') {
                    setValue('overTime', null);
                }
                if (section === 'agenda') {
                    setValue('agenda', []);
                }
                if (section === 'note') {
                    setValue('note', null);
                }
                if (section === 'experience') {
                    setValue('idealSkill', null);
                }
                if (section === 'localities') {
                    setValue('localityId', null);
                }
                if (section === 'transfer') {
                    setTargetWsId(undefined);
                }
                return prev.slice(0, _index).concat(prev.slice(_index + 1, undefined));
            }
            // if we are turning on automatic pause, we have to remove manual pause
            if (section === 'automatic-pause') {
                const manualPauseIndex = prev.findIndex((sec) => sec === 'manual-pause');
                setValue('pausesFixed', false);
                // @ts-expect-error
                setValue('pauses', dummyShift?.pauses.filter(util_1.isValue) || []);
                return ['automatic-pause'].concat(prev
                    .slice(0, manualPauseIndex)
                    .concat(prev.slice(manualPauseIndex + 1, undefined)));
            }
            // if we are turning on emergency we have to turn off standby flag on shift
            if (section === 'emergency') {
                setValue('standBy', true);
            }
            if (section === 'overtime') {
                const overTimeIsOrdered = (0, filtered_content_for_organization_1.getFilteredContentForOrganization)('overtime_unpaid_false_should_be_ordered', false, {});
                setValue('overTime', {
                    ordered: overTimeIsOrdered,
                    paidTOIL: false,
                    unpaid: false,
                    start: null,
                    duration: null,
                });
            }
            return [...prev, section];
        });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        setToggledSections,
        dummyShift,
        getValues,
        setValue,
        triggerSubscriptions,
        workspace,
        setTargetWsId,
    ]);
    const context = {
        formControl: control,
        setValue,
        getValues,
        initialData,
        formType: initialData?.type ?? 'live',
        selectedTab,
        setSelectedTab,
        toggledSections,
        toggleSection,
        handleSubmit,
        dummyShift,
        loading,
        initialLoad,
        saving,
        errors,
        disabledEditation,
        applyShiftTemplate,
        currentTemplate,
        templateName,
        setTemplateName,
        targetWsId,
        setTargetWsId,
        addSectionDisabledListener: add,
        removeSectionDisabledListener: remove,
        onSubmit,
        copiesCount,
        setCopiesCount,
        transferRole,
        transferStatus,
        transferType,
        transferMaster,
        transferSlave,
        transferGroups,
        transferActionId: existingShiftData?.requestPendingAction,
    };
    return (react_1.default.createElement(exports.ShiftContext.Provider, { value: context }, children?.(context)));
};
exports.ShiftProvider = ShiftProvider;
const useShift = () => {
    const shiftContext = react_1.default.useContext(exports.ShiftContext);
    if ((0, util_1.isUndefined)(shiftContext)) {
        throw new Error('useShift must be used within <ShiftProvider>');
    }
    return shiftContext;
};
exports.useShift = useShift;
